import type { Writable } from "svelte/store";
import type { SmbConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// The browser cannot speak SMB. File calls go through /api/smb. The user and
// password are request headers, not query parameters.
// Without a share in the config, "/" lists the shares of the server.

class FileSystemSmb extends FS {
  supportsDirRename = true;

  constructor(config: SmbConfig) {
    super(config);
  }

  get smbConfig(): SmbConfig {
    return this.config as SmbConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await smbList(this.smbConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the password header. Fetch through
    // the same-origin proxy and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = smbURL(this.smbConfig, "/api/smb/download", "path", path);
    let xhr = await smbSend("GET", url, this.smbConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = smbURL(this.smbConfig, "/api/smb/upload", "path", path);
    await smbSend("PUT", url, this.smbConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = smbURL(this.smbConfig, "/api/smb/rename", "path", path, "newPath", newPath);
    await smbCall(this.smbConfig, url, "POST");
  }

  async createDirectory(path: string): Promise<void> {
    let url = smbURL(this.smbConfig, "/api/smb/mkdir", "path", path);
    await smbCall(this.smbConfig, url, "POST");
  }

  async deleteFile(path: string): Promise<void> {
    let url = smbURL(this.smbConfig, "/api/smb/delete", "path", path);
    await smbCall(this.smbConfig, url, "POST");
  }
}

export async function testSmbConnection(config: SmbConfig): Promise<void> {
  await smbCall(config, smbURL(config, "/api/smb/test"));
}

async function smbList(config: SmbConfig, dir: string): Promise<any[][]> {
  let js = await smbCall(config, smbURL(config, "/api/smb/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("smb list failed");
  }
  return js;
}

export function smbURL(config: SmbConfig, path: string, ...args: string[]): string {
  return mkURL(path, "host", config.host, "port", config.port, "domain", config.domain, "share", config.share, ...args);
}

function encodeSecret(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function smbHeaders(config: SmbConfig): Record<string, string> {
  let headers: Record<string, string> = {};
  if (config.user) {
    headers["X-Smb-User"] = encodeSecret(config.user);
  }
  if (config.password) {
    headers["X-Smb-Password"] = encodeSecret(config.password);
  }
  return headers;
}

async function smbCall(config: SmbConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, { method, headers: smbHeaders(config) });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `smb ${rsp.status}`);
  }
  return js;
}

function smbSend(
  method: string,
  url: string,
  config: SmbConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  let headers = smbHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      smbErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`smb ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("smb request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function smbErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `smb ${xhr.status}`;
}

export function makeFileSystemSmb(config: SmbConfig): FileSystemSmb {
  return new FileSystemSmb(config);
}
